// tslint:disable: max-classes-per-file
import { BigNumber } from '@0x/utils'
import { Asset, getInfo } from 'app-lib/assetUtils'
import errorUtils from 'app-lib/errorUtils'
import providerUtils from 'app-lib/providerUtils'
import * as mobx from 'mobx'
import { IMigration, migrationList } from '../services/MigrateService'
import RootStore from './RootStore'

type BzxAsset = Asset.BZRX

export type MigrateAsset = Asset.BZRX

interface IMigrationProps {
  amountToConvert: string
  approvalPending: boolean
  balancesPending: boolean
  conversionError: null | Error
  conversionPending: boolean
  details: IMigration
  enabled: boolean
  liquidityAvailable: BigNumber
  liquidityLoaded: boolean
  spendingApproval: BigNumber
  walletBalanceFrom: BigNumber
  walletBalanceTo: BigNumber
}

/**
 * Model for a migration from one toke to another
 * eg: BGOV -> BZRX, BZRX -> OOKI, ...
 */
export class Migration implements IMigrationProps {
  amountToConvert = ''
  approvalPending = false
  balancesPending = false
  conversionError: null | Error = null
  conversionPending = false
  details: IMigration
  enabled = true
  liquidityAvailable = new BigNumber(0)
  liquidityLoaded = false
  rootStore: RootStore
  spendingApproval = new BigNumber(0)
  walletBalanceFrom = new BigNumber(0)
  walletBalanceTo = new BigNumber(0)

  get needsApproval() {
    return this.walletBalanceFrom.gt(0) && this.spendingApproval.lt(this.walletBalanceFrom)
  }

  get amountAsBigNumber() {
    return new BigNumber(this.amountToConvert || 0)
  }

  get conversionAmount() {
    return this.amountAsBigNumber.times(this.details.conversionRatio)
  }

  get canConvert() {
    const { isConnected } = this.rootStore.web3Connection
    return (
      isConnected &&
      !this.needsApproval &&
      this.amountAsBigNumber.gt(0) &&
      this.walletBalanceFrom.gte(this.amountToConvert) &&
      this.enabled
    )
  }

  get migrationChainDetails() {
    const { chainId } = this.rootStore.web3Connection
    if (!chainId) {
      return
    }
    return this.details.chains[chainId]
  }

  /**
   * Helper to set values through mobx actions.
   */
  set(prop: keyof IMigrationProps, value: any) {
    ;(this[prop] as any) = value
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  assign(props: Partial<IMigrationProps>) {
    Object.assign(this, props)
  }

  setMaxAmount() {
    this.amountToConvert = this.rootStore.userWallet
      .findTokenBalance(this.details.fromAsset)
      .toFixed()
  }

  clearData() {
    this.amountToConvert = ''
    this.liquidityAvailable = new BigNumber(0)
    this.spendingApproval = new BigNumber(0)
    this.walletBalanceFrom = new BigNumber(0)
    this.liquidityLoaded = false
  }

  /**
   * If a converter has limited amount of tokens for conversion
   * Currently fails silently as it is not critical but could be reviewed.
   */
  async getLiquidityAvailable() {
    try {
      if (!this.migrationChainDetails) {
        return
      }
      const { address, decimals } = getInfo(
        this.migrationChainDetails.chainId,
        this.details.toAsset
      )
      const amount = await providerUtils.getErc20Balance(
        this.rootStore.fulcrumProvider,
        address,
        this.migrationChainDetails.converterAddress
      )
      const liquidityAvailable = amount.div(10 ** decimals)
      this.assign({ liquidityAvailable, liquidityLoaded: true })
      return liquidityAvailable
    } catch (err) {
      console.error(err)
    }
  }

  async setSpendingApproval() {
    if (!this.migrationChainDetails) {
      return
    }
    const { spendingApprovals } = this.rootStore
    this.assign({ approvalPending: true })
    const approval = await spendingApprovals.setInfiniteApprovalForToken(
      this.details.fromAsset,
      this.migrationChainDetails.converterAddress
    )
    if (approval) {
      this.assign({ spendingApproval: approval.amount })
    }
    this.assign({ approvalPending: false })
  }

  async refreshTokensAmounts() {
    if (!this.migrationChainDetails) {
      return
    }
    try {
      this.assign({ balancesPending: true })
      const { userWallet, spendingApprovals } = this.rootStore
      const { fromAsset, toAsset } = this.details
      const [from, to] = await userWallet.getBalances([fromAsset, toAsset])
      this.assign({
        amountToConvert: from.amount.toFixed(),
        walletBalanceFrom: from.amount,
        walletBalanceTo: to.amount,
      })
      const approval = await spendingApprovals.checkApproval(
        fromAsset,
        this.migrationChainDetails.converterAddress
      )
      this.set('spendingApproval', approval.amount)
      if (this.migrationChainDetails.limitedLiquidity) {
        this.getLiquidityAvailable()
      }
    } catch (err) {
      console.error(err)
    } finally {
      this.assign({ balancesPending: false })
    }
  }

  async convert() {
    try {
      const { transactionStore, services } = this.rootStore
      this.assign({ conversionPending: true, conversionError: null })
      const txSteps = services.migrateService.migrate(this.details, this.amountAsBigNumber)
      const tx = await transactionStore.run('migrate-token', txSteps)
      if (tx.status === 'success') {
        this.refreshTokensAmounts()
      }
    } catch (err) {
      errorUtils.decorateError(err, { title: 'Could not convert' })
      this.assign({ conversionError: err })
    } finally {
      this.assign({ conversionPending: false })
    }
  }

  constructor(migrationInfo: IMigration, rootStore: RootStore) {
    this.details = migrationInfo
    this.rootStore = rootStore
    mobx.makeAutoObservable(this, undefined, { autoBind: true, deep: false })
  }
}

interface IMigrationStoreProps {
  errorConversion: Error | null
  migrateAssets: BzxAsset[]
  autoSyncData: boolean
}

export default class MigrationStore implements IMigrationStoreProps {
  public rootStore: RootStore
  errorConversion: Error | null = null
  migrateAssets: BzxAsset[] = []
  autoSyncData = false
  migrations: Migration[]

  /**
   * Helper to set values through mobx actions.
   */
  set(prop: keyof IMigrationStoreProps, value: any) {
    ;(this[prop] as any) = value
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  assign(props: Partial<IMigrationStoreProps>) {
    Object.assign(this, props)
  }

  clearData() {
    this.migrations.forEach((migration) => migration.clearData())
  }

  init() {
    mobx.autorun(() => {
      if (this.autoSyncData) {
        const { chainId, walletAddress } = this.rootStore.web3Connection
        this.migrations.forEach((migration, index) => {
          migration.clearData()
          if (walletAddress && chainId && migration.details.chains[chainId]) {
            setTimeout(() => {
              migration.refreshTokensAmounts()
            }, index * 500)
          }
        })
      }
    })
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    this.migrations = [new Migration(migrationList.BZRX_TO_OOKI, rootStore)]
    mobx.makeAutoObservable(this, { rootStore: false }, { autoBind: true, deep: false })
  }
}
